import axios from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import {
  PODBORKI_API_TOKEN,
  PODBORKI_CREATE_API_URL,
  PODBORKI_LIST_API_URL,
  getFilmPodborkaChangeApiUrl,
  getPodborkaDetailsApiUrl,
  getPodborkaMoviesApiUrl,
} from "../api/podborki";
import {
  Podborka,
  PodborkaApiResponse,
  PodborkaMovie,
  PodborkaMoviesApiResponse,
  PodborkiApiResponse,
} from "../types/podborki";

class PodborkiStore {
  podborki: Podborka[] = [];
  loading = false;
  error: string | null = null;
  selectedPodborka: Podborka | null = null;
  movies: PodborkaMovie[] = [];
  detailsLoading = false;
  detailsError: string | null = null;
  createLoading = false;
  createError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchPodborki() {
    this.loading = true;
    this.error = null;

    try {
      const response = await axios.get<PodborkiApiResponse>(
        PODBORKI_LIST_API_URL,
        {
          headers: {
            "Http-X-Token": PODBORKI_API_TOKEN,
          },
        }
      );

      if (!response.data.success || !Array.isArray(response.data.data)) {
        throw new Error("Сервис вернул некорректный список подборок");
      }

      runInAction(() => {
        this.podborki = response.data.data;
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : "Неизвестная ошибка";

      runInAction(() => {
        this.error = `Не удалось загрузить подборки: ${message}`;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async fetchPodborkaDetails(id: number) {
    this.detailsLoading = true;
    this.detailsError = null;
    this.selectedPodborka = null;
    this.movies = [];

    try {
      const [detailsResponse, moviesResponse] = await Promise.all([
        axios.get<PodborkaApiResponse>(getPodborkaDetailsApiUrl(id), {
          headers: {
            "Http-X-Token": PODBORKI_API_TOKEN,
          },
        }),
        axios.get<PodborkaMoviesApiResponse>(getPodborkaMoviesApiUrl(id), {
          headers: {
            "Http-X-Token": PODBORKI_API_TOKEN,
          },
        }),
      ]);

      if (!detailsResponse.data.success || !detailsResponse.data.data) {
        throw new Error("Сервис вернул некорректные данные подборки");
      }

      if (!moviesResponse.data.success || !Array.isArray(moviesResponse.data.data?.items)) {
        throw new Error("Сервис вернул некорректный список фильмов");
      }

      runInAction(() => {
        this.selectedPodborka = detailsResponse.data.data;
        this.movies = moviesResponse.data.data.items;
      });
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : "Неизвестная ошибка";

      runInAction(() => {
        this.detailsError = `Не удалось загрузить подборку: ${message}`;
      });
    } finally {
      runInAction(() => {
        this.detailsLoading = false;
      });
    }
  }

  async createPodborka(file: File, name: string, sort: number, description: string) {
    this.createLoading = true;
    this.createError = null;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("sort", String(sort));
    formData.append("description", description);

    try {
      const response = await axios.post(PODBORKI_CREATE_API_URL, formData, {
        headers: {
          "Http-X-Token": PODBORKI_API_TOKEN,
        },
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Сервис не подтвердил создание подборки");
      }

      return true;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : "Неизвестная ошибка";

      runInAction(() => {
        this.createError = `Не удалось создать подборку: ${message}`;
      });
      return false;
    } finally {
      runInAction(() => {
        this.createLoading = false;
      });
    }
  }

  async changeFilmPodborka(
    filmId: number,
    podborkaId: number,
    status: 0 | 1
  ) {
    try {
      const response = await axios.get(
        getFilmPodborkaChangeApiUrl(filmId, podborkaId, status),
        {
          headers: {
            "Http-X-Token": PODBORKI_API_TOKEN,
          },
        }
      );

      if (response.data?.success === false) {
        throw new Error(response.data?.message || "Сервис не подтвердил изменение");
      }

      return true;
    } catch (error) {
      const message = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
          ? error.message
          : "Неизвестная ошибка";

      runInAction(() => {
        this.detailsError = `Не удалось изменить состав подборки: ${message}`;
      });
      return false;
    }
  }
}

const podborkiStore = new PodborkiStore();

export default podborkiStore;
